import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { callSheetApi } from "@/lib/api";
import { CheckCircle2, Loader2, Clapperboard, AlertCircle } from "lucide-react";

export default function ConfirmPage() {
  const { csId, token } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    (async () => {
      try {
        const result = await callSheetApi.confirm(csId, token);
        setState({ loading: false, success: true, data: result });
      } catch (e) {
        setState({
          loading: false,
          success: false,
          error: e?.response?.data?.detail || "Could not confirm — link may be invalid or expired.",
        });
      }
    })();
  }, [csId, token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-zinc-950 relative z-10">
      <div className="w-full max-w-md text-center">
        <div className="mb-8 flex justify-center">
          <img
            src="https://customer-assets.emergentagent.com/job_shoot-organizer-1/artifacts/3jmotfdo_WC%20Logo%202018%20FULL%20Outlined%20WHITE.png"
            alt="Whiplash"
            className="h-10 opacity-90"
          />
        </div>

        {state.loading ? (
          <div className="text-zinc-400">
            <Loader2 className="w-7 h-7 animate-spin text-amber-500 mx-auto mb-4" />
            <div className="text-sm">Confirming receipt…</div>
          </div>
        ) : state.success ? (
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-md p-10 animate-fade-up">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" strokeWidth={2} />
            </div>
            <div className="text-overline text-emerald-400/80 mb-2">Confirmed</div>
            <h1 className="font-heading text-3xl font-light text-zinc-100 mb-2">
              Thank you{state.data?.name ? `, ${firstName(state.data.name)}` : ""}!
            </h1>
            <p className="text-zinc-400 text-sm mb-6 leading-relaxed">
              Your receipt of the call sheet for{" "}
              <strong className="text-zinc-200">{state.data?.project_name}</strong>
              {(state.data?.day_label || (state.data?.day_number && state.data.day_number > 1))
                ? ` (${state.data.day_label?.trim() || `Day ${state.data.day_number}`})`
                : state.data?.day_of
                  ? ` (${state.data.day_of})`
                  : ""} has been recorded.
              The production team will see a checkmark next to your name.
            </p>
            <div className="text-xs text-zinc-500 font-mono">
              Confirmed at {formatTimestamp(state.data?.confirmed_at)}
            </div>
            <div className="mt-8 pt-6 border-t border-zinc-800/60">
              <Clapperboard className="w-4 h-4 text-amber-500/70 mx-auto mb-2" />
              <div className="text-overline text-zinc-600">
                Whiplash Film &amp; Creative
              </div>
            </div>
          </div>
        ) : (
          <div className="border border-rose-500/30 bg-rose-500/5 rounded-md p-10">
            <div className="w-16 h-16 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-rose-400" strokeWidth={2} />
            </div>
            <h1 className="font-heading text-2xl font-light text-zinc-100 mb-2">
              Couldn&apos;t confirm
            </h1>
            <p className="text-zinc-400 text-sm">{state.error}</p>
            <Link
              to="/"
              className="inline-block mt-6 text-xs text-amber-500 hover:text-amber-400 uppercase tracking-widest"
            >
              Go home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function firstName(n) {
  return (n || "").trim().split(/\s+/)[0];
}

function formatTimestamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
